import EmberObject from '@ember/object';

/**
  The `Transform` class is used to serialize and deserialize model
  attributes when they are saved or loaded from an
  adapter. Subclassing `Transform` is useful for creating custom
  attributes. All subclasses of `Transform` must implement a
  `serialize` and a `deserialize` method.

  Example

  ```js [app/transforms/temperature.js]

  // Converts centigrade in the JSON to fahrenheit in the app
  export default class TemperatureTransform {
    deserialize(serialized, options) {
      return (serialized *  1.8) + 32;
    }

    serialize(deserialized, options) {
      return (deserialized - 32) / 1.8;
    }

    static create() {
      return new this();
    }
  }
  ```

  Usage

  ```js [app/models/requirement.js]
  import { Model, attr } from '@warp-drive/legacy/model';

  export default class RequirementModel extends Model {
    @attr('string') name;
    @attr('temperature') temperature;
  }
  ```

  The options passed into the `attr` function when the attribute is
  declared on the model is also available in the transform.

  ```js [app/models/post.js]
  import { Model, attr } from '@warp-drive/legacy/model';

  export default class PostModel extends Model {
    @attr('string') title;
    @attr('markdown', {
      markdown: {
        gfm: false,
        sanitize: true
      }
    })
    markdown;
  }
  ```

  ```js [app/transforms/markdown.js]
  export default class MarkdownTransform {
    serialize(deserialized, options) {
      return deserialized.raw;
    }

    deserialize(serialized, options) {
      let markdownOptions = options.markdown || {};

      return marked(serialized, markdownOptions);
    }

    static create() {
      return new this();
    }
  }
  ```

  @class Transform
  @public
 */
const Transform = EmberObject;

// eslint-disable-next-line @typescript-eslint/no-unused-vars

/**
  The `BooleanTransform` class is used to serialize and deserialize
  boolean attributes on Ember Data record objects. This transform is
  used when `'boolean'` is passed as the type parameter to the
  {@link attr}function.

  Usage

  ```js [app/models/user.js]
  import { Model, attr } from '@warp-drive/legacy/model';

  export default class UserModel extends Model {
    @attr('boolean') isAdmin;
    @attr('string') name;
    @attr('string') email;
  }
  ```

  By default, the boolean transform only allows for values of `true` or
  `false`. You can opt into allowing `null` values for
  boolean attributes via `attr('boolean', { allowNull: true })`

  ```js [app/models/user.js]
  import { Model, attr } from '@warp-drive/legacy/model';

  export default class UserModel extends Model {
    @attr('string') email;
    @attr('string') username;
    @attr('boolean', { allowNull: true }) wantsWeeklyEmail;
  }
  ```

  @public
 */
class BooleanTransform {
  deserialize(serialized, options) {
    if ((serialized === null || serialized === undefined) && options?.allowNull === true) {
      return null;
    }
    if (typeof serialized === 'boolean') {
      return serialized;
    } else if (typeof serialized === 'string') {
      return /^(true|t|1)$/i.test(serialized);
    } else if (typeof serialized === 'number') {
      return serialized === 1;
    } else {
      return false;
    }
  }
  serialize(deserialized, options) {
    if ((deserialized === null || deserialized === undefined) && options?.allowNull === true) {
      return null;
    }
    return Boolean(deserialized);
  }
  static create() {
    return new this();
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars

/**
 The `DateTransform` class is used to serialize and deserialize
 date attributes on Ember Data record objects. This transform is used
 when `'date'` is passed as the type parameter to the
 {@link attr} function. It uses the [`ISO 8601`](https://en.wikipedia.org/wiki/ISO_8601)
 standard.

 ```js [app/models/score.js]
 import Model, { attr, belongsTo } from '@ember-data/model';

 export default class ScoreModel extends Model {
    @attr('number') value;
    @belongsTo('player') player;
    @attr('date') date;
  }
 ```

  @public
 */

class DateTransform {
  deserialize(serialized, _options) {
    if (typeof serialized === 'string') {
      let offset = serialized.indexOf('+');
      if (offset !== -1 && serialized.length - 5 === offset) {
        offset += 3;
        return new Date(serialized.slice(0, offset) + ':' + serialized.slice(offset));
      }
      return new Date(serialized);
    } else if (typeof serialized === 'number') {
      return new Date(serialized);
    } else if (serialized === null || serialized === undefined) {
      // if the value is null return null
      // if the value is not present in the data return undefined
      return serialized;
    } else {
      return null;
    }
  }
  serialize(date, _options) {
    // @ts-expect-error isNaN accepts date as it is coercible
    if (date instanceof Date && !isNaN(date)) {
      return date.toISOString();
    } else {
      return null;
    }
  }
  static create() {
    return new this();
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars

function isNumber(value) {
  return value === value && value !== Infinity && value !== -Infinity;
}
/**
  The `NumberTransform` class is used to serialize and deserialize
  numeric attributes on Ember Data record objects. This transform is
  used when `number` is passed as the type parameter to the
  {@link attr} function.

  Usage

  ```js [app/models/score.js]
  import Model, { attr, belongsTo } from '@ember-data/model';

  export default class ScoreModel extends Model {
    @attr('number') value;
    @belongsTo('player') player;
    @attr('date') date;
  }
  ```

  @public
 */
class NumberTransform {
  deserialize(serialized, _options) {
    if (serialized === '' || serialized === null || serialized === undefined) {
      return null;
    } else {
      const transformed = Number(serialized);
      return isNumber(transformed) ? transformed : null;
    }
  }
  serialize(deserialized, _options) {
    if (deserialized === '' || deserialized === null || deserialized === undefined) {
      return null;
    } else {
      const transformed = Number(deserialized);
      return isNumber(transformed) ? transformed : null;
    }
  }
  static create() {
    return new this();
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars

/**
  The `StringTransform` class is used to serialize and deserialize
  string attributes on Ember Data record objects. This transform is
  used when `string` is passed as the type parameter to the
  {@link attr} function.

  Usage

  ```js [app/models/user.js]
  import Model, { attr, belongsTo } from '@ember-data/model';

  export default class UserModel extends Model {
    @attr('boolean') isAdmin;
    @attr('string') name;
    @attr('string') email;
  }
  ```

  @public
 */
class StringTransform {
  deserialize(serialized, _options) {
    return !serialized && serialized !== '' ? null : String(serialized);
  }
  serialize(deserialized, _options) {
    return !deserialized && deserialized !== '' ? null : String(deserialized);
  }
  static create() {
    return new this();
  }
}
export { BooleanTransform, DateTransform, NumberTransform, StringTransform, Transform };